import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { previewInvite, acceptInvite } from "./services/inviteService";
import { UserProfileContext } from "../../context/UserContext";
import { getResolvedUserType, hasUserType, USER_TYPES } from "../../utils/userRole";
import EstateList from "../../components/AccountSwitcher/EstateList";
import loginImage from "../login/images/landing-hero.jpg";

const InvitationPreview = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const profileCtx = useContext(UserProfileContext);
  const { userProfile, activeAccount, setActiveAccount, refreshProfile } =
    profileCtx || {};

  // Presence-only: a stale/invalid token (expired, or invalidated by a
  // secret rotation) still passes this check, since it doesn't verify the
  // token — only handleAccept's 401 handling below catches that case.
  const isAuthenticated = !!localStorage.getItem("WMS#221#");

  useEffect(() => {
    let mounted = true;
    const fetchPreview = async () => {
      setLoading(true);
      try {
        const data = await previewInvite(token);
        if (mounted) setInvite(data?.data || data);
      } catch (err) {
        if (mounted) setError(err?.message || JSON.stringify(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchPreview();
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`);
      return;
    }

    setAccepting(true);
    try {
      await acceptInvite(token);

      // Refresh profile to pick up the newly added estate
      const freshProfile = refreshProfile ? await refreshProfile() : null;
      const resolvedProfile = freshProfile || userProfile;

      const estates = Array.isArray(resolvedProfile?.estates)
        ? resolvedProfile.estates
        : [];
      const isVtu = hasUserType(resolvedProfile, USER_TYPES.VTU_USER);

      toast({
        title: "Invitation accepted!",
        description: `You have joined "${invite?.estateName || "the estate"}".`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      // Check for an explicit ?next= redirect in the URL
      const searchParams = new URLSearchParams(location.search);
      const nextPath = searchParams.get("next");

      if (nextPath) {
        navigate(nextPath);
      } else if (estates.length > 1 || (estates.length > 0 && isVtu)) {
        // Multiple accounts — let the user pick which one to use
        onOpen();
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      if (err?.statusCode === 401) {
        // The stored token exists but the backend rejected it (expired, or
        // invalidated by a JWT secret rotation) — isAuthenticated above only
        // checks presence, not validity. Send the resident to log in again
        // instead of showing the backend's generic "Unable to verify
        // token." message as a dead end.
        localStorage.removeItem("WMS#221#");
        const next = encodeURIComponent(location.pathname + location.search);
        navigate(`/login?next=${next}`);
        return;
      }
      setError(err?.message || JSON.stringify(err));
    } finally {
      setAccepting(false);
    }
  };

  const handleSelectEstate = (estate) => {
    const acc = {
      type: "estate",
      estateId: estate?.estateId?._id || estate?.estateId,
      name: estate?.estateId?.name || estate?.name,
    };
    setActiveAccount?.(acc);
    toast({
      title: `Switched to ${acc.name}`,
      status: "success",
      duration: 2500,
      isClosable: true,
    });
    onClose();
    navigate("/dashboard");
  };

  const handleSelectVtu = () => {
    setActiveAccount?.({ type: "vtu", name: "VTU Account" });
    toast({
      title: "Switched to VTU account",
      status: "success",
      duration: 2500,
      isClosable: true,
    });
    onClose();
    navigate("/vtu");
  };

  const showHero = useBreakpointValue({ base: false, lg: true });

  if (loading)
    return (
      <Box p={6} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );

  if (error)
    return (
      <Box p={6} maxW="720px" mx="auto">
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );

  if (!invite)
    return (
      <Box p={6} maxW="720px" mx="auto">
        <Text>No invitation found.</Text>
      </Box>
    );

  const { estateName, email } = invite;
  const estates = Array.isArray(userProfile?.estates)
    ? userProfile.estates
    : [];
  const isVtu = hasUserType(userProfile, USER_TYPES.VTU_USER);

  return (
    <>
      <Box
        minH={isAuthenticated ? "calc(100vh - 70px)" : "100vh"}
        bg="white"
        overflow="auto"
      >
        <Box
          display="flex"
          flexDirection={["column", "column", "row"]}
          w="100%"
          minH="inherit"
          alignItems="stretch"
        >
          {showHero && (
            <Box
              w={["100%", "45%", "40%"]}
              minH="inherit"
              backgroundImage={`url(${loginImage})`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
            />
          )}
          <Box
            p={8}
            w={["100%", "65%", "60%"]}
            mx="auto"
            display="flex"
            alignItems="center"
          >
            <VStack align="start" spacing={4} maxW="720px" w="100%" mx="auto">
              <Heading size="lg">
                Invitation to join {estateName || "an estate"}
              </Heading>
              <Text>
                You were invited by <strong>an admin</strong> to join the estate{" "}
                <Text as="span" fontWeight="600">
                  "{estateName}"
                </Text>
                .
              </Text>
              {email && (
                <Text>
                  Invitation sent to: <strong>{email}</strong>
                </Text>
              )}
              <HStack pt={4}>
                <Button
                  colorScheme="blue"
                  onClick={handleAccept}
                  isLoading={accepting}
                >
                  {isAuthenticated ? "Accept Invitation" : "Log in to Accept"}
                </Button>
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  Back
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>

      {/* Select account modal — shown after a successful accept when the user has multiple accounts */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select an Account to Continue</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Text mb={4} color="gray.600">
              You joined <strong>{estateName}</strong>. Choose which account to
              use now.
            </Text>
            <EstateList
              estates={estates}
              onSelectEstate={handleSelectEstate}
              isVtu={isVtu}
              onSelectVtu={handleSelectVtu}
              activeAccount={activeAccount}
              buttonLabel="Use this account"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default InvitationPreview;
